import { pdf } from '@react-pdf/renderer';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    fetchFundRequestDetail,
    fetchFundRequestHistory,
} from '~/services/fund-requests';
import { FundRequest } from '~/types/fundrequests';
import { FundRequestDocument } from './pdf/FundRequestDocument';
import JSZip from 'jszip';
import { FundRequestExpenseTypes } from '~/enums/fundRequest';
import { authHeaders } from '~/services/auth';

type HistoryItem = {
    user_id: number;
    person_id: number;
    person_name: string;
    status: string;
    updated_date: string;
    description?: string;
};

type ExpenseItem = {
    uploads?: { image_url?: string; imageUrl?: string; filename?: string; fileName?: string }[];
    [key: string]: unknown;
};

// Extract all uploads from a fund request
const extractUploads = (fundRequest: FundRequest | null) => {
    const keys = [
        'transactions_food',
        'transactions_medical',
        'transactions_insurance',
        'transactions_medications',
        'transactions_other',
        'transactions_relocation_lodging',
        'transactions_travel',
        'transactions_caregiving',
        'transactions_physical_therapy',
        'transactions_fundraising',
    ] as const;

    const results: {
        fileName: string;
        imageUrl: string;
        formKey: string;
        expenseCategory: FundRequestExpenseTypes;
        uploadIndex: number;
    }[] = [];

    keys.forEach((key) => {
        const transactions = fundRequest?.[key] as ExpenseItem[] | undefined;
        transactions?.forEach((transaction, transactionIndex) => {
            if ('uploads' in transaction && transaction.uploads) {
                transaction.uploads.forEach((uploadItem, uploadIndex) => {
                    const imageUrl = uploadItem.image_url || uploadItem.imageUrl;
                    const fileName =
                        uploadItem.filename ||
                        uploadItem.fileName ||
                        `file_${uploadIndex + 1}`;

                    if (imageUrl) {
                        const categoryMap: Record<string, FundRequestExpenseTypes> = {
                            transactions_food: FundRequestExpenseTypes.FOOD,
                            transactions_medical: FundRequestExpenseTypes.DEFAULT,
                            transactions_insurance: FundRequestExpenseTypes.DEFAULT,
                            transactions_medications: FundRequestExpenseTypes.DEFAULT,
                            transactions_other: FundRequestExpenseTypes.DEFAULT,
                            transactions_relocation_lodging: FundRequestExpenseTypes.DEFAULT,
                            transactions_travel: FundRequestExpenseTypes.TRAVEL,
                            transactions_caregiving: FundRequestExpenseTypes.DEFAULT,
                            transactions_physical_therapy: FundRequestExpenseTypes.DEFAULT,
                            transactions_fundraising: FundRequestExpenseTypes.DEFAULT,
                        };

                        results.push({
                            fileName,
                            imageUrl,
                            formKey: `${key}[${transactionIndex}]`,
                            expenseCategory: categoryMap[key],
                            uploadIndex,
                        });
                    }
                });
            }
        });
    });

    // Add ACH document if it exists
    if (fundRequest?.ach_request?.ach_document_url) {
        results.push({
            fileName: fundRequest.ach_request.ach_document_filename || 'ACH_Document',
            imageUrl: fundRequest.ach_request.ach_document_url,
            formKey: 'ach_document',
            expenseCategory: FundRequestExpenseTypes.DEFAULT,
            uploadIndex: 0,
        });
    }

    return results;
};

export const FundRequestPDF = () => {
    const [fundRequest, setFundRequest] = useState<FundRequest | null>(null);
    const [requestHistory, setRequestHistory] = useState<HistoryItem[]>([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const { id } = useParams();

    useEffect(() => {
        const fetchFundRequest = async () => {
            if (!id) return;
            try {
                const resp = await fetchFundRequestDetail(id);
                console.log('Fund request data:', resp);
                setFundRequest(resp);
            } catch (error) {
                console.error('Error fetching fund request:', error);
            }
        };

        const getHistory = async () => {
            if (!id) return;
            const resp = await fetchFundRequestHistory(id);
            // New API returns history items directly (no serialized_data wrapper)
            const history = resp.filter((item: HistoryItem | null) => {
                // Filter out null, undefined, and empty objects
                if (!item || typeof item !== 'object') {
                    return false;
                }
                // Ensure the item has required properties
                return item.person_name && item.updated_date;
            });
            setRequestHistory(history);
            return history;
        };

        fetchFundRequest();
        getHistory();
    }, [id]);

    const handleDownloadZip = async () => {
        if (!fundRequest || !fundRequest.vendor || !fundRequest.fund_info?.patient) return;

        setIsDownloading(true);

        try {
            const zip = new JSZip();
            const { patient } = fundRequest.fund_info;
            const ntaf_id = patient?.cf_patient_ntaf_id;

            // Generate PDF blob
            const pdfBlob = await pdf(
                <FundRequestDocument
                    fundRequest={fundRequest}
                    requestHistory={requestHistory}
                />
            ).toBlob();

            // Add PDF to zip
            const pdfFilename = `${ntaf_id}_FundRequest_${fundRequest.id}.pdf`;
            zip.file(pdfFilename, pdfBlob);

            // Get all uploaded documents
            const uploads = extractUploads(fundRequest);

            // Create a documents folder in the zip
            const documentsFolder = zip.folder('documents');

            if (uploads.length > 0 && documentsFolder) {
                // Fetch and add all documents to zip
                const promises = uploads.map(async (file, index) => {
                    try {
                        // Check if URL is from S3
                        const isS3Url = file.imageUrl.includes('s3.amazonaws.com') || file.imageUrl.includes('.s3.');
                        
                        // For S3 URLs: use plain fetch with no headers to avoid CORS preflight
                        // For non-S3 URLs: use auth headers
                        const response = await fetch(file.imageUrl, isS3Url ? {} : {
                            headers: authHeaders(),
                            credentials: 'include',
                        });
                        
                        if (!response.ok) {
                            throw new Error(
                                `HTTP ${response.status}: ${response.statusText}`
                            );
                        }
                        
                        const blob = await response.blob();

                        // Create a unique filename to avoid duplicates
                        const extension = file.fileName?.split('.').pop() || '';
                        const nameWithoutExt =
                            file.fileName?.replace(/\.[^/.]+$/, '') ||
                            `file_${index + 1}`;
                        const uniqueFilename = `${nameWithoutExt}_${index + 1}.${extension}`;

                        documentsFolder.file(uniqueFilename, blob);
                    } catch (error) {
                        // Log the error for debugging
                        console.error(`Failed to download file ${file.fileName}:`, error);
                        
                        // Add a text file noting the error
                        const errorFileName = `ERROR_${file.fileName || `file_${index + 1}`}.txt`;
                        documentsFolder.file(
                            errorFileName,
                            `Failed to download this file: ${(error as Error).message}\nOriginal URL: ${file.imageUrl}`
                        );
                    }
                });

                await Promise.all(promises);
            }

            // Generate the zip file
            const zipBlob = await zip.generateAsync({ type: 'blob' });

            // Create download link
            const link = document.createElement('a');
            const url = URL.createObjectURL(zipBlob);
            link.href = url;

            // Create filename with NTAF ID and fund request ID
            const zipFilename = `${ntaf_id}_FundRequest_${fundRequest.id}_Package.zip`;
            link.download = zipFilename;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error creating zip file:', error);
            alert('Failed to create zip file. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    if (!fundRequest || !fundRequest.vendor || !fundRequest.fund_info?.patient) {
        return <div>Loading...</div>;
    }

    const { patient } = fundRequest.fund_info;
    const uploads = extractUploads(fundRequest);

    return (
        <div className='p-4'>
            <div className='rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='mb-4'>
                    <h3 className='text-xl font-semibold text-black dark:text-white'>
                        Fund Request for {patient?.cf_patient_ntaf_id}
                    </h3>
                </div>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    <div>
                        <p className='text-sm font-medium text-black dark:text-white'>
                            Patient Name
                        </p>
                        <p className='text-sm text-black dark:text-white'>
                            {patient?.person?.first_name}{' '}
                            {patient?.person?.last_name}
                        </p>
                    </div>
                    <div>
                        <p className='text-sm font-medium text-black dark:text-white'>
                            Campaign Type
                        </p>
                        <p className='text-sm text-black dark:text-white'>
                            {patient?.campaign_type}
                        </p>
                    </div>
                    <div>
                        <p className='text-sm font-medium text-black dark:text-white'>
                            Request ID
                        </p>
                        <p className='text-sm text-black dark:text-white'>
                            {fundRequest.id}
                        </p>
                    </div>
                    <div>
                        <p className='text-sm font-medium text-black dark:text-white'>
                            Status
                        </p>
                        <p className='text-sm text-black dark:text-white'>
                            {fundRequest.fund_request_status?.label || 'N/A'}
                        </p>
                    </div>
                </div>
                <div className='mt-6'>
                    <button
                        onClick={handleDownloadZip}
                        disabled={isDownloading}
                        className='flex w-full justify-center rounded bg-primary p-3 font-medium text-white hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                        {isDownloading ? (
                            <div className='flex items-center gap-2'>
                                <div className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent'></div>
                                <span>
                                    {uploads.length > 0
                                        ? `Downloading Package (PDF + ${uploads.length} document${uploads.length !== 1 ? 's' : ''})...`
                                        : 'Downloading PDF...'}
                                </span>
                            </div>
                        ) : (
                            <span>
                                {uploads.length > 0
                                    ? `Download Package (PDF + ${uploads.length} document${uploads.length !== 1 ? 's' : ''})`
                                    : 'Download PDF'}
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
